import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "../../../components/Modal";
import { ListToolbar } from "../../../components/ListToolbar";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import {
  ApiError,
  createBillingMilestone,
  deleteBillingMilestone,
  getBillingMilestones,
  updateBillingMilestone,
  type BillingMilestone,
  type BillingMilestonePayload,
} from "../../../marcos/marcosApi";
import { generateInvoiceFromMilestone } from "../../../contasReceber/contasReceberApi";

interface WorkMilestonesTabProps {
  workId: number;
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value).replace(/\./g, " ");
};

const formatPercent = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString(APP_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export function WorkMilestonesTab({ workId }: WorkMilestonesTabProps) {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState<BillingMilestone[]>([]);
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [invoiceNotice, setInvoiceNotice] = useState<string | null>(null);

  const [descricao, setDescricao] = useState("");
  const [percentagem, setPercentagem] = useState("");
  const [valor, setValor] = useState("");
  const [dataPrevista, setDataPrevista] = useState("");

  const [editingMilestone, setEditingMilestone] = useState<BillingMilestone | null>(null);
  const [draft, setDraft] = useState<BillingMilestonePayload | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const loadMilestones = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setMilestones(await getBillingMilestones(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os marcos de faturação.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadMilestones();
  }, [loadMilestones]);

  const resetForm = () => {
    setDescricao("");
    setPercentagem("");
    setValor("");
    setDataPrevista("");
  };

  // Create entry-point: clear the form and open the (create) modal.
  const openCreate = () => {
    resetForm();
    setError(null);
    setIsCreateOpen(true);
  };

  // Close whichever variant of the form modal is open (create or edit).
  const closeForm = () => {
    setIsCreateOpen(false);
    setEditingMilestone(null);
    setDraft(null);
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (!descricao.trim()) {
      setError("A descrição é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createBillingMilestone({
        workId,
        description: descricao.trim(),
        percentage: parseNumber(percentagem),
        value: parseNumber(valor),
        expectedDate: dataPrevista !== "" ? dataPrevista : null,
      });
      setMilestones((prev) => [...prev, created]);
      closeForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar o marco de faturação.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (milestoneId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este marco de faturação?")) return;
    setDeletingId(milestoneId);
    setError(null);
    try {
      await deleteBillingMilestone(milestoneId);
      setMilestones((prev) => prev.filter((item) => item.id !== milestoneId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setMilestones((prev) => prev.filter((item) => item.id !== milestoneId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o marco de faturação.");
        loadMilestones(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerateInvoice = async (milestone: BillingMilestone) => {
    if (generatingId !== null || milestone.isBilled) return;
    setGeneratingId(milestone.id);
    setError(null);
    setInvoiceNotice(null);
    try {
      const invoice = await generateInvoiceFromMilestone(milestone.id);
      setInvoiceNotice(`Rascunho de fatura ${invoice.number} criado a partir do marco.`);
      // Refresh so the milestone flips to "Faturado".
      await loadMilestones(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível gerar a fatura.",
      );
    } finally {
      setGeneratingId(null);
    }
  };

  const openEdit = (milestone: BillingMilestone) => {
    setEditingMilestone(milestone);
    setDraft({
      workId,
      description: milestone.description ?? "",
      percentage: milestone.percentage ?? null,
      value: milestone.value ?? null,
      expectedDate: toInputDate(milestone.expectedDate),
    });
  };

  const saveEdit = async () => {
    if (!editingMilestone || !draft) return;
    if (!draft.description.trim()) {
      setError("A descrição é obrigatória.");
      return;
    }
    setError(null);
    try {
      const updated = await updateBillingMilestone(editingMilestone.id, {
        workId,
        description: draft.description.trim(),
        percentage: draft.percentage ?? null,
        value: draft.value ?? null,
        expectedDate: draft.expectedDate ? draft.expectedDate : null,
      });
      setMilestones((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      closeForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o marco de faturação.");
    }
  };

  const normalizedFilter = filter.trim().toLowerCase();
  const shown =
    normalizedFilter === ""
      ? milestones
      : milestones.filter((milestone) =>
          [
            milestone.description ?? "",
            formatPercent(milestone.percentage),
            formatCurrency(milestone.value),
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  const totalValue = shown.reduce((sum, milestone) => sum + (milestone.value ?? 0), 0);

  return (
    <div className="space-y-6">
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar marcos…"
        summary={<span className="text-secondary-text text-sm">{shown.length} marco(s) · Total {formatCurrency(totalValue)}</span>}
      >
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Novo marco
        </button>
      </ListToolbar>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      {invoiceNotice && (
        <div
          className="border border-border bg-green-500/10 text-green-700 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
          style={{ borderRadius: "var(--radius)" }}
        >
          <span>{invoiceNotice}</span>
          <button
            type="button"
            className="text-primary underline"
            onClick={() => navigate("/contas-a-receber")}
          >
            Abrir Contas a Receber
          </button>
        </div>
      )}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[720px] w-full">
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3 text-right w-[120px]">Percentagem</th>
              <th className="px-4 py-3 text-right w-[140px]">Valor</th>
              <th className="px-4 py-3 w-[120px]">Data Prevista</th>
              <th className="px-4 py-3 w-[140px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">{filter !== "" ? "Sem resultados para o filtro." : "Sem marcos de faturação. Use \"+ Novo marco\" para adicionar o primeiro."}</td></tr>
            ) : shown.map((milestone) => (
              <tr key={milestone.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-4 py-3 whitespace-normal break-words">{milestone.description || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatPercent(milestone.percentage)}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatCurrency(milestone.value)}</td>
                <td className="px-4 py-3 break-words">{formatDate(milestone.expectedDate)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 flex-wrap items-center">
                    {milestone.isBilled ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary border border-primary/20"
                        style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius)", fontWeight: "var(--font-weight-medium)" }}
                        title={milestone.invoiceNumber ? `Fatura ${milestone.invoiceNumber}` : "Marco faturado"}
                      >
                        Faturado{milestone.invoiceNumber ? ` · ${milestone.invoiceNumber}` : ""}
                      </span>
                    ) : (
                      <button
                        className="text-primary underline disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button"
                        disabled={generatingId === milestone.id}
                        onClick={() => handleGenerateInvoice(milestone)}
                      >
                        {generatingId === milestone.id ? "A gerar…" : "Gerar fatura"}
                      </button>
                    )}
                    <button className="text-primary underline" type="button" onClick={() => openEdit(milestone)}>Editar</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === milestone.id} onClick={() => handleDelete(milestone.id)}>{deletingId === milestone.id ? "A eliminar…" : "Eliminar"}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(isCreateOpen || (editingMilestone && draft)) && (
        <Modal
          title={editingMilestone ? "Editar marco de faturação" : "Novo marco de faturação"}
          onClose={closeForm}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button type="button" onClick={closeForm} className="px-4 py-2 min-h-[44px] border border-border" style={{ borderRadius: "var(--radius-button)" }}>Cancelar</button>
              <button
                type="button"
                disabled={!editingMilestone && isSaving}
                onClick={editingMilestone ? saveEdit : handleAdd}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {!editingMilestone && isSaving ? "A adicionar…" : "Guardar"}
              </button>
            </>
          }
        >
          {editingMilestone && draft ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Descrição *
                <input placeholder="Descrição" value={draft.description ?? ""} maxLength={300} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Percentagem (%)
                <input placeholder="0" value={draft.percentage ?? ""} onChange={(e) => setDraft({ ...draft, percentage: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor (€)
                <input placeholder="0" value={draft.value ?? ""} onChange={(e) => setDraft({ ...draft, value: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Data Prevista
                <input type="date" value={draft.expectedDate ?? ""} onChange={(e) => setDraft({ ...draft, expectedDate: e.target.value || null })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Descrição *
                <input value={descricao} onChange={(event) => setDescricao(event.target.value)} maxLength={300} placeholder="Descrição" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Percentagem (%)
                <input type="number" min="0" step="0.01" value={percentagem} onChange={(event) => setPercentagem(event.target.value)} placeholder="0" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor (€)
                <input type="number" min="0" step="0.01" value={valor} onChange={(event) => setValor(event.target.value)} placeholder="0" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Data Prevista
                <input type="date" value={dataPrevista} onChange={(event) => setDataPrevista(event.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
